import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import Request from "../Request";
import { BaseReportParsedData, MoneyDataItem } from '../../interfaces/reports/BaseReport.interface';

export interface TableToPdfOptions {
    columns: Array<string>;
    data: Array<Array<string | number>>;
    fileName: string;
    title?: string;
    subtitle?: string;
    orientation?: 'p' | 'l'; // portrait or landscape
    pageSize?: string; // 'a4', 'letter', etc.
}

const ALL_VERTS = [
    'locationCode',
    'classCode',
    'category',
    'parentClientClassCode',
    'month',
    "vertical",
    'brand',
];

const ALWAYS_KEEP_FIELDS = [
    'locationName',
    'className',
    'imu',
    'costComplement',
    'categorySortOrder',
]

function groupByHierarchy<T>(
    data: T[],
    hierarchy: string[],
    skipSort = false,
): T[] {
    if (!data.length) return [];

    // Detect all MoneyDataItem fields dynamically
    const first = data[0] as any;
    const moneyFields = Object.keys(first).filter(
        key => first[key] instanceof MoneyDataItem
    );

    // Detect other non-hierarchy fields
    const otherFields = Object.keys(first).filter(
        key => !ALL_VERTS.includes(key) && !(first[key] instanceof MoneyDataItem) && !ALWAYS_KEEP_FIELDS.includes(key)
    );

    type Row = Record<string, any>;
    const result: Row[] = [];

    // Helper: Sum money fields for a group of rows
    function sumMoneyFields(rows: Row[]): Row {
        const sums: Row = {};
        for (const field of moneyFields) {
            let total = 0;
            for (const row of rows) {
                const val = row[field];
                if (val instanceof MoneyDataItem) {
                    total += Number(val.value) || 0;
                }
            }
            sums[field] = new MoneyDataItem(total);
        }
        return sums;
    }

    function makeTotalRow(vertValues: Record<string, string | null>, groupRows: Row[]): Row {
        const row: Row = {};
        // Hierarchy/vert columns: set value or ""
        for (const vert of ALL_VERTS) {
            row[vert] = vertValues[vert] ?? "";
        }
        // Money fields: sum
        Object.assign(row, sumMoneyFields(groupRows));
        // Always keep fields: copy from first row
        for (const field of ALWAYS_KEEP_FIELDS) {
            if (!(field in groupRows[0])) continue;
            row[field] = groupRows[0][field] || "";
        }
        // Non-vert, non-money fields: null
        // for (const field of otherFields) {
        //     row[field] = null;
        // }
        return row;
    }

    // Recursive grouping, sorts at every level
    function recursiveGroup(rows: Row[], depth: number, prefix: Record<string, string | null>) {
        result.push(makeTotalRow(prefix, rows));

        if (depth < hierarchy.length) {
            const key = hierarchy[depth];

            // Special handling for parentClientClassCode
            if (key === 'parentClientClassCode') {
                // Group parents (parentClientClassCode == null)
                const parentRows = rows.filter(row => row.parentClientClassCode == null);

                // Group children by parentClientClassCode (non-null)
                const childGroups: Record<string, Row[]> = {};
                for (const row of rows) {
                    if (row.parentClientClassCode != null) {
                        const parentCode = row.parentClientClassCode;
                        if (!childGroups[parentCode]) childGroups[parentCode] = [];
                        childGroups[parentCode].push(row);
                    }
                }

                // Inject each parent row into its own child group (so it shows alongside its children)
                for (const parentRow of parentRows) {
                    const parentCode: string | undefined = parentRow.classCode;
                    if (!parentCode) continue;
                    if (!childGroups[parentCode]) {
                        childGroups[parentCode] = [];
                    }
                    // Put parent at the start of its group
                    childGroups[parentCode].unshift(parentRow);
                }

                // Process parent rows first (top-level aggregation of all parents)
                if (parentRows.length) {
                    const nextPrefix = { ...prefix, [key]: null };
                    recursiveGroup(parentRows, depth + 1, nextPrefix);
                }

                // Then process each parent group (now includes parent + its children)
                const sortedParentCodes = Object.keys(childGroups).sort();
                for (const parentCode of sortedParentCodes) {
                    const childRows = childGroups[parentCode];
                    if (childRows.length === 0) continue;
                    const nextPrefix = { ...prefix, [key]: parentCode };
                    recursiveGroup(childRows, depth + 1, nextPrefix);
                }
            } else {
                // Default grouping for other fields
                const groups: Record<string, Row[]> = {};
                for (const row of rows) {
                    const groupVal = row[key] || '';
                    if (!groups[groupVal]) groups[groupVal] = [];
                    groups[groupVal].push(row);
                }
                let sortedKeys = Object.keys(groups);

                // Special-case: if grouping by category and any group has categorySortOrder, sort by that integer.
                // Otherwise fall back to numeric/alphanumeric sort (same behavior as before).
                if (key === 'category') {
                    const anyHasOrder = Object.values(groups).some(g => g.length && g[0].categorySortOrder != null);
                    if (anyHasOrder) {
                        sortedKeys.sort((a, b) => {
                            const aRows = groups[a];
                            const bRows = groups[b];
                            const aOrder = (aRows && aRows[0] && aRows[0].categorySortOrder != null) ? Number(aRows[0].categorySortOrder) : null;
                            const bOrder = (bRows && bRows[0] && bRows[0].categorySortOrder != null) ? Number(bRows[0].categorySortOrder) : null;

                            if (aOrder != null && bOrder != null) return aOrder - bOrder;
                            if (aOrder != null) return -1; // items with explicit order come before those without
                            if (bOrder != null) return 1;

                            // fallback to numeric or locale compare
                            if (!isNaN(Number(a)) && !isNaN(Number(b))) {
                                return Number(a) - Number(b);
                            }
                            return String(a).localeCompare(String(b));
                        });
                    } else {
                        sortedKeys = sortedKeys.sort((a, b) => {
                            if (!isNaN(Number(a)) && !isNaN(Number(b))) {
                                return Number(a) - Number(b);
                            }
                            return String(a).localeCompare(String(b));
                        });
                    }
                } else if (key != 'vertical') {
                    sortedKeys = Object.keys(groups).sort((a, b) => {
                        if (!isNaN(Number(a)) && !isNaN(Number(b))) {
                            return Number(a) - Number(b);
                        }
                        return String(a).localeCompare(String(b));
                    });
                }
                for (const groupKey of sortedKeys) {
                    const nextPrefix = { ...prefix, [key]: groupKey };
                    recursiveGroup(groups[groupKey], depth + 1, nextPrefix);
                }
            }
        }
    }

    // Sort all input data first by full hierarchy for stable output
    if (!skipSort)
        data.sort((a: any, b: any) => {
            for (const key of hierarchy) {
                if (a[key] < b[key]) return -1;
                if (a[key] > b[key]) return 1;
            }
            return 0;
        });

    // Start recursive grouping from top
    recursiveGroup(data as unknown as Row[], 0, {});

    return result as T[];
}

class ReportService {
    static async getClassPerformanceReportData({ clientId, startEpochMonth = null, endEpochMonth = null, isMtd = false }: { clientId: string | number, startEpochMonth: number | null, endEpochMonth: number | null, isMtd?: boolean }) {
        try {
            const params = {
                startEpochMonth: startEpochMonth ?? null,
                endEpochMonth: endEpochMonth ?? null,
                isMtd: isMtd ? 1 : 0
            }
            const response = await Request.get(`/api/clients/${clientId}/reports/class-performance`, params);
            return response
        } catch (error) {
            console.error(error);
        }
    }

    static async getInventoryActionReportData({ clientId, epochMonth = null, monthsForward = 3 }: { clientId: string | number, epochMonth: number | null, monthsForward?: number }) {
        try {
            const params = epochMonth ? { epochMonth: epochMonth, monthsForward } : { monthsForward };
            const response = await Request.get(`/api/clients/${clientId}/reports/inventory-action`, params);
            return response
        } catch (error) {
            console.error(error);
        }
    }

    // Shopify Pro reports stream a raw JSON array by default (not wrapped in { data, ... }).
    // Passing includeChildClasses switches the endpoint to also return parentLevelData
    // (child-class rows tagged with their parentClientClassCode), in which case the
    // response comes back as { data, parentLevelData } instead of a raw array.
    static async getInventoryHealthReportData({ clientId, epochMonth = null, timeMode = "Monthly", includeChildClasses = false }: { clientId: string | number, epochMonth: number | null, timeMode?: string, includeChildClasses?: boolean }) {
        try {
            const params: Record<string, any> = {};
            if (epochMonth) params.targetEpochMonth = epochMonth;
            if (timeMode) params.timeMode = timeMode.toLowerCase();
            if (includeChildClasses) params.includeChildClasses = true;

            const response = await Request.get(`/api/clients/${clientId}/shopify-pro/reports/inventory-health/monthly`, params);

            if (Array.isArray(response)) {
                return { data: response, parentLevelData: [] };
            }
            return { data: response?.data ?? [], parentLevelData: response?.parentLevelData ?? [] };
        } catch (error) {
            console.error(error);
        }
    }

    static async getPlanOTBReportData({ clientId, epochMonth = null, timeMode = "Monthly", includeChildClasses = false }: { clientId: string | number, epochMonth: number | null, timeMode?: string, includeChildClasses?: boolean }) {
        try {
            const params: Record<string, any> = {};
            if (epochMonth) params.targetEpochMonth = epochMonth;
            if (timeMode) params.timeMode = timeMode.toLowerCase();
            if (includeChildClasses) params.includeChildClasses = true;

            const response = await Request.get(`/api/clients/${clientId}/shopify-pro/reports/plan-otb/monthly`, params);

            if (Array.isArray(response)) {
                return { data: response, parentLevelData: [] };
            }
            return { data: response?.data ?? [], parentLevelData: response?.parentLevelData ?? [] };
        } catch (error) {
            console.error(error);
        }
    }

    static async getWeeklyActionReportData({ clientId, epochMonth = null, timeMode = "Monthly", includeChildClasses = false }: { clientId: string | number, epochMonth: number | null, timeMode?: string, includeChildClasses?: boolean }) {
        try {
            const params: Record<string, any> = {};
            if (epochMonth) params.targetEpochMonth = epochMonth;
            if (timeMode) params.timeMode = timeMode.toLowerCase();
            if (includeChildClasses) params.includeChildClasses = true;

            const response = await Request.get(`/api/clients/${clientId}/shopify-pro/reports/weekly-action/monthly`, params);

            if (Array.isArray(response)) {
                return { data: response, parentLevelData: [] };
            }
            return { data: response?.data ?? [], parentLevelData: response?.parentLevelData ?? [] };
        } catch (error) {
            console.error(error);
        }
    }

    static async getBuyerPerformanceReportData({ clientId, startEpochMonth = null, endEpochMonth = null }: { clientId: string | number, startEpochMonth: number | null, endEpochMonth: number | null }) {
        try {
            const params = {
                startEpochMonth: startEpochMonth ?? null,
                endEpochMonth: endEpochMonth ?? null,
            }
            const response = await Request.get(`/api/clients/${clientId}/reports/buyer-performance`, params);
            return response
        } catch (error) {
            console.error(error);
        }
    }

    static async getInventoryVarianceReportData({ clientId, startEpochMonth = null, endEpochMonth = null, isMtd = false }: { clientId: string | number, startEpochMonth: number | null, endEpochMonth: number | null, isMtd?: boolean }) {
        try {
            const params = {
                startEpochMonth: startEpochMonth ?? null,
                endEpochMonth: endEpochMonth ?? null,
                isMtd: isMtd ? 1 : 0
            }
            const response = await Request.get(`/api/clients/${clientId}/reports/inventory-variance`, params);
            return response
        } catch (error) {
            console.error(error);
        }
    }

    static async getProgressDashboardReportData({ clientId, epochMonth = null }: { clientId: string | number, epochMonth: number | null }) {
        try {
            const params = epochMonth ? { epochMonth: epochMonth } : {};
            const response = await Request.get(`/api/clients/${clientId}/reports/progress-dashboard`, params);
            return response
        } catch (error) {
            console.error(error);
        }
    }

    static async getCurrentMonthAdvancedTrendData({ clientId, epochMonth = null }: { clientId: string | number, epochMonth: number | null }) {
        try {
            const params: any = epochMonth ? { epochMonth: epochMonth } : {};
            const response = await Request.get(`/api/clients/${clientId}/reports/progress-dashboard/current-month-advanced-trend`, params);
            return response;
        } catch (error) {
            console.error(error);
        }
    }

    static async getProgressDashboardAdvancedTrendData({ clientId, epochMonth = null }: { clientId: string | number, epochMonth: number | null }) {
        return ReportService.getCurrentMonthAdvancedTrendData({ clientId, epochMonth });
    }

    static async getOtbRecapReportData({ clientId, startEpochMonth, endEpochMonth }: { clientId: string | number, startEpochMonth: number, endEpochMonth: number }) {
        try {
            const params: any = { startEpochMonth, endEpochMonth };
            const response = await Request.get(`/api/clients/${clientId}/reports/otb-recap`, params);
            return response
        } catch (error) {
            console.error(error);
        }
    }

    static async getInventoryPerformanceReportData({ clientId, startEpochMonth, endEpochMonth, isMtd = false }: { clientId: string | number, startEpochMonth: number | null, endEpochMonth: number | null, isMtd?: boolean }) {
        try {
            const params = {
                startEpochMonth: startEpochMonth ?? null,
                endEpochMonth: endEpochMonth,
                isMtd: isMtd ? 1 : 0
            }
            const response = await Request.get(`/api/clients/${clientId}/reports/inventory-performance`, params);
            return response
        } catch (error) {
            console.error(error);
        }
    }

    static async getGlobalAnalyticsReportData({ clientId, startEpochMonth = null, endEpochMonth = null, monthsBack = 3 }: { clientId: string | number, startEpochMonth: number | null, endEpochMonth: number | null, monthsBack?: number }) {
        try {
            const params = {
                startEpochMonth: startEpochMonth ?? null,
                endEpochMonth: endEpochMonth ?? null,
                monthsBack,
            };
            const response = await Request.get(`/api/clients/${clientId}/reports/global-analytics-beta`, params);
            return response;
        } catch (error) {
            console.error(error);
        }
    }

    static async getDataValidationReportData({ clientId }: { clientId: string | number }) {
        try {
            const response = await Request.get(`/api/clients/${clientId}/reports/data-validation`);
            return response;
        } catch (error) {
            console.error(error);
        }
    }

    // Unlike its siblings above, this endpoint is not namespaced under /api/clients/{clientId}/...;
    // client_id is passed as a query param instead and authorized server-side against the logged-in user.
    // locationCodes/classCodes are `locationCode`/`classCode` values from Reporting.HIERARCHY (the
    // same client-scoped codes /api/clients/{clientId}/dropdown-options returns for filter
    // dropdowns elsewhere in the app) -- NOT numeric ids.
    static async getVendorPerformanceReportData({ clientId, from, to, groupBy, locationCodes, classCodes, vendorIds, categoryIds, excludeLocations, excludeClasses, excludeCategories, excludeVendors, minSales, includeLy, onOrderSpan, onOrderStartDate, onOrderEndDate }: {
        clientId: string | number;
        from: string;
        to: string;
        groupBy: string;
        locationCodes?: string[];
        classCodes?: string[];
        vendorIds?: string[];
        categoryIds?: string[];
        // "Exclude selected instead" for the Location/Class/Category/Vendor filters -- flips the
        // corresponding *Codes/*Ids list from an IN match to a NOT IN match server-side.
        excludeLocations?: boolean;
        excludeClasses?: boolean;
        excludeCategories?: boolean;
        excludeVendors?: boolean;
        minSales?: number | null;
        includeLy?: boolean;
        // How far into the future an open PO's expected delivery can fall and still count toward
        // On-Order $/Units.
        onOrderSpan?: string;
        onOrderStartDate?: string;
        onOrderEndDate?: string;
    }) {
        try {
            const params: Record<string, any> = { client_id: clientId, from, to, group_by: groupBy, include_ly: includeLy ?? true };
            if (locationCodes?.length) params.location_codes = locationCodes;
            if (classCodes?.length) params.class_codes = classCodes;
            if (vendorIds?.length) params.vendor_ids = vendorIds;
            if (categoryIds?.length) params.category_ids = categoryIds;
            if (excludeLocations) params.exclude_locations = true;
            if (excludeClasses) params.exclude_classes = true;
            if (excludeCategories) params.exclude_categories = true;
            if (excludeVendors && vendorIds?.length) params.exclude_vendors = true;
            if (minSales != null) params.min_sales = minSales;
            if (onOrderSpan) params.on_order_span = onOrderSpan;
            if (onOrderStartDate) params.on_order_start_date = onOrderStartDate;
            if (onOrderEndDate) params.on_order_end_date = onOrderEndDate;
            const response = await Request.get('/api/reports/vendor-performance', params);
            return response;
        } catch (error) {
            console.error(error);
        }
    }

    // Every vendor in the client's catalog for the Vendor Performance Vendor filter; [] on failure
    // so the filter is empty rather than the report failing.
    static async getVendorPerformanceVendorOptions(clientId: string | number): Promise<string[]> {
        try {
            const response = await Request.get('/api/reports/vendor-performance/vendors', { client_id: clientId });
            return response?.vendors ?? [];
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    /**
     * Format a number as a percent (no decimals, with % sign)
     */
    static formatPercent(value: number | string): string {
        const num = Number(value);
        if (isNaN(num)) return '';
        return `${Math.round(num)}%`;
    }

    /**
     * Format a number as currency (no decimals, with $ and commas)
     */
    static formatCurrency(value: number | string): string {
        const num = Number(value);
        if (isNaN(num)) return '';
        return Math.round(num).toLocaleString();
    }

    /**
     * Format a number as a ratio (one decimal place)
     */
    static formatRatio(value: number | string): string {
        const num = Number(value);
        if (isNaN(num) || !isFinite(num)) return '';
        return (Math.round(num * 10) / 10).toFixed(1);
    }

    static async tableToPdf({
        columns,
        data,
        fileName,
        title,
        subtitle,
        orientation = 'l',
        pageSize = 'a0'
    }: TableToPdfOptions) {
        try {
            // Use a reasonably wide custom page that still displays well on most screens.
            // Width/height are in points (pt). 1100pt wide (~15.3in) is a good balance.
            const defaultPageWidth = 1100;
            const defaultPageHeight = 792;
            const doc = new jsPDF({ orientation, unit: 'pt', format: [defaultPageWidth, defaultPageHeight] });
            doc.setFontSize(20);
            const marginLeft = 20;
            const marginRight = 20;
            let startY = 40;
            if (title) {
                doc.text(title, doc.internal.pageSize.getWidth() / 2, startY, { align: 'center' });
                startY += 30;
            }
            if (subtitle) {
                doc.setFontSize(14);
                doc.text(subtitle, doc.internal.pageSize.getWidth() / 2, startY, { align: 'center' });
                startY += 30;
                doc.setFontSize(20);
            }

            // Use full width between margins; this keeps the table centered and prevents right overflow
            const pageWidth = doc.internal.pageSize.getWidth();
            // subtract 1pt to avoid any right-edge bleed from stroke width rounding
            const printableWidth = Math.max(0, Math.floor(pageWidth - marginLeft - marginRight - 1));
            const numColumns = Math.max(1, columns.length);
            // Adaptive widths: measure header and sample body cell text to derive relative weights
            doc.setFontSize(8);
            const minColWidth = 50;
            const maxColWidth = Math.min(300, Math.floor(printableWidth * 0.32));
            const sampleRows = Math.min(25, data.length); // limit measurement for performance
            const measurementPadding = 12; // extra space to reduce wrap risk

            const rawWeights: number[] = columns.map((header, colIdx) => {
                const headerText = String(header || '').trim();
                let maxDataTextWidth = 0;
                for (let r = 0; r < sampleRows; r++) {
                    const cell = data[r][colIdx];
                    if (cell === null || cell === undefined) continue;
                    const cellStr = String(cell).trim();
                    if (!cellStr) continue;
                    const w = doc.getTextWidth(cellStr);
                    if (w > maxDataTextWidth) maxDataTextWidth = w;
                }
                const headerWidth = doc.getTextWidth(headerText);
                const base = Math.max(headerWidth, maxDataTextWidth) + measurementPadding;
                // Clamp early to keep proportions saner
                return Math.min(Math.max(base, minColWidth), maxColWidth);
            });

            // If all weights are zero (unlikely), fall back to equal widths
            let totalWeight = rawWeights.reduce((a, b) => a + b, 0);
            let columnWidths: number[];
            if (totalWeight === 0) {
                const fallback = Math.floor(printableWidth / numColumns);
                columnWidths = new Array(numColumns).fill(fallback);
            } else {
                // Scale weights so sum exactly equals printableWidth
                columnWidths = rawWeights.map(w => (w / totalWeight) * printableWidth);
                // Apply min/max after scaling; track adjustments
                for (let i = 0; i < columnWidths.length; i++) {
                    columnWidths[i] = Math.min(Math.max(columnWidths[i], minColWidth), maxColWidth);
                }
                // Re-normalize after clamping
                totalWeight = columnWidths.reduce((a, b) => a + b, 0);
                if (totalWeight !== printableWidth && totalWeight > 0) {
                    const scale = printableWidth / totalWeight;
                    for (let i = 0; i < columnWidths.length; i++) {
                        columnWidths[i] = Math.min(Math.max(columnWidths[i] * scale, minColWidth), maxColWidth);
                    }
                }
                // Final integer rounding & distribute any rounding remainder to first columns
                let intTotal = 0;
                columnWidths = columnWidths.map(w => {
                    const iw = Math.floor(w);
                    intTotal += iw;
                    return iw;
                });
                let remainder = printableWidth - intTotal;
                let idx = 0;
                while (remainder > 0) {
                    if (columnWidths[idx] < maxColWidth) {
                        columnWidths[idx] += 1;
                        remainder--;
                    }
                    idx = (idx + 1) % columnWidths.length;
                }
            }

            autoTable(doc, {
                head: [columns],
                body: data,
                startY: startY,
                // Equal margins keep the table centered
                margin: { left: marginLeft, right: marginRight },
                styles: {
                    fontSize: 8,
                    cellPadding: { top: 2, right: 1, bottom: 2, left: 1 },
                    overflow: 'linebreak',
                    halign: 'center',
                    valign: 'middle',
                    lineWidth: 0.3,
                    lineColor: [180, 180, 180],
                    // Let columnStyles numeric widths control sizing and wrap text
                    cellWidth: 'wrap',
                },
                headStyles: {
                    fillColor: [51, 51, 51],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    halign: 'center',
                    valign: 'middle',
                    lineWidth: 0.3,
                    lineColor: [120, 120, 120],
                    fontSize: 8,
                },
                bodyStyles: {
                    halign: 'center',
                    valign: 'middle',
                    lineWidth: 0.3,
                    lineColor: [180, 180, 180],
                    fontSize: 8,
                },
                tableLineWidth: 0.3,
                tableLineColor: [120, 120, 120],
                tableWidth: printableWidth,
                columnStyles: {
                    ...Object.fromEntries(
                        columns.map((col, index) => [index, { cellWidth: columnWidths[index], overflow: 'linebreak' }])
                    )
                },
                didDrawPage: (data) => {
                    const pageSize = doc.internal.pageSize;
                    const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
                    doc.setFontSize(8);
                    doc.text(
                        `Page ${data.pageNumber} of ${doc.getNumberOfPages()}`,
                        data.settings.margin.left,
                        pageHeight - 10
                    );
                    const date = new Date().toLocaleDateString();
                    doc.text(
                        `Generated on: ${date}`,
                        pageSize.width - 120,
                        pageHeight - 10
                    );
                }
            });
            const pdfUrl = doc.output("bloburl");
            window.open(pdfUrl, "_blank");
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    // Add method for CSV export
    static tableToCSV({ columns, data, fileName }: { columns: Array<string>, data: Array<Array<string | number>>, fileName: string }) {
        try {
            // Create CSV content
            let csvContent = columns.join(',') + '\n';

            // Add data rows
            data.forEach(row => {
                // Properly handle commas and quotes in cell values
                const formattedRow = row.map(cell => {
                    const cellStr = String(cell);
                    // If cell contains comma, quote, or newline, wrap in quotes and escape any quotes
                    if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                        return `"${cellStr.replace(/"/g, '""')}"`;
                    }
                    return cellStr;
                });
                csvContent += formattedRow.join(',') + '\n';
            });

            // Create a blob and download link
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `${fileName}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    static calculateImu({ receivedRetailAcrossLocations, receivedCostAcrossLocations }: { receivedRetailAcrossLocations: number, receivedCostAcrossLocations: number }) {
        if (receivedRetailAcrossLocations === 0) return 0;
        return (receivedRetailAcrossLocations - receivedCostAcrossLocations) / receivedRetailAcrossLocations;
    }

    static calculateMmu({ receivedRetailAcrossLocations, receivedCostAcrossLocations, markdownPercent }: { receivedRetailAcrossLocations: number, receivedCostAcrossLocations: number, markdownPercent: number }) {
        const imu = ReportService.calculateImu({ receivedRetailAcrossLocations, receivedCostAcrossLocations });
        return imu - (markdownPercent * (1 - imu));
    }

    static calculateCop({ inflowCost, sales }: { inflowCost: number, sales: number }) {
        if (sales === 0) return 0;
        return inflowCost / sales;
    }

    /**
     * Main "rollup" function. Returns a flat array with total/subtotal rows at all levels.
     * Data columns are detected as MoneyDataItem instances.
     * Non-vert, non-MoneyDataItem columns are set to null in total/subtotal rows.
     */
    static groupByHierarchy<T extends BaseReportParsedData>(
        data: Array<T>,
        hierarchy: Array<string>,
        skipSort = false,
    ): Array<T> {
        return groupByHierarchy<T>(
            data,
            hierarchy,
            skipSort,
        );
    }

    static hierarchyItemToVerticalDisplayNames<T extends BaseReportParsedData>(
        row: T,
        hierarchy: Array<string>,
    ): Array<string> {
        hierarchy = hierarchy.filter(h => ALL_VERTS.includes(h));
        return hierarchy.map(h => {
            const key = h.toLowerCase();
            switch (key) {
                case 'locationcode':
                    return row.locationCode === '' ? 'TOTAL' : `${row.locationCode} ${row.locationName}`;
                case 'classcode':
                    return row.classCode === '' ? 'TOTAL' : `${row.classCode} ${row.className}`;
                case 'category':
                    return row.category === '' ? 'TOTAL' : row.category || '';
                case 'parentclientclasscode':
                    if (row.parentClientClassCode === null) {
                        // use classcode
                        return row.classCode === '' ? 'TOTAL' : `${row.classCode} ${row.className}`;
                    } else {
                        return row.parentClientClassCode === '' ? 'TOTAL' : `${row.parentClientClassCode}`;
                    }
                case 'vertical':
                    return row.vertical === '' ? 'TOTAL' : row.vertical || '';
                case 'brand':
                    return (row as any).brand === '' ? 'TOTAL' : ((row as any).brand || '');
                default:
                    return '';
            }
        });
    }

    /**
     * Returns table row style class name based on hierarchy and row data's first N items.
     * 
     * Applies styling based on how many 'TOTAL' entries are present in the first N items (N = hierarchy.length).
     * - If all are 'TOTAL': primary
     * - If all but one: medium-light
     * - If all but two: light
     * - Otherwise: null
     * 
     * @param row 
     * @param hierarchy 
     */
    static getRowClassName(row: Array<string | number>, hierarchy: Array<string>): string | null {
        const N = hierarchy.length;
        if (N <= 1) {
            return null;
        }

        // Check first N items for 'TOTAL'
        let totalCount = 0;
        for (let i = 0; i < N; i++) {
            const val = row[i];
            if (typeof val === 'string' && ['total',].includes(val.toLowerCase())) {
                totalCount++;
            }
        }

        if (totalCount === 0) {
            return null;
        }

        if (totalCount === N) {
            return 'font-bold bg-tw-primary text-white';
        }
        if (totalCount === N - 1) {
            return 'font-bold bg-tw-primary-medium-light text-white';
        }
        if (totalCount === N - 2) {
            return 'font-bold bg-[#af7cc4] text-white';
        }
        return null;
    }

    static sortAndFilterData<T extends BaseReportParsedData>
        (
            data: Array<T>,
            hierarchy: string[],
            locationFilter: string[],
            classFilter: string[],
            categoryFilter: string[],
            skipSort = false,
        ): Array<T> {
        // Filter data based on selected filters
        let filteredData = data.filter(item => {
            const matchesLocation = locationFilter.length === 0 || locationFilter.includes(item.locationCode);
            const matchesClass = hierarchy.includes('parentClientClassCode') ?
                (classFilter.length === 0 || classFilter.includes(item.classCode) || (item.parentClientClassCode && classFilter.includes(item.parentClientClassCode)))
                : (classFilter.length === 0 || classFilter.includes(item.classCode));
            const matchesCategory = categoryFilter.length === 0 || (item.category !== undefined && categoryFilter.includes(item.category));
            return matchesLocation && matchesClass && matchesCategory;
        });


        // Sort data based on hierarchy
        return ReportService.groupByHierarchy(filteredData, hierarchy, skipSort);
    }

    // Calculate updated otb
    static calculateUpdatedOtb({ invBomNM, currInv, sales, salesPlan, mdRemaining, onOrder, format = true }: { invBomNM: number, currInv: number, sales: number, salesPlan: number, mdRemaining: number, onOrder: number, format?: boolean }): number | string {
        const expectedSales = salesPlan - sales < 0 ? 0 : salesPlan - sales;
        const updatedOtb = invBomNM - (currInv - (expectedSales + mdRemaining)) - onOrder;
        return format ? ReportService.formatCurrency(updatedOtb) : updatedOtb;
    }

    // Calculate trended otb
    static calculateTrendedOtb({ invBomNM, currInv, sales, salesPlan, mdRemaining, onOrder, trend, format = true }: { invBomNM: number, currInv: number, sales: number, salesPlan: number, mdRemaining: number, onOrder: number, trend: number, format?: boolean }): number | string {
        // if trend is 0, return updated otb
        if (trend === 0) {
            return ReportService.calculateUpdatedOtb({ invBomNM, currInv, sales, salesPlan, mdRemaining, onOrder, format });
        }
        const inflowTrend = ReportService.calculateInflowTrend({ invBomNM, currInv, sales, salesPlan, mdRemaining, trend, format: false }) as number;
        const trendedOtb = inflowTrend - onOrder;
        return format ? ReportService.formatCurrency(trendedOtb) : trendedOtb;
    }

    // Calculate Inflow Updated
    static calculateInflowUpdated({ invBomNM, currInv, sales, salesPlan, mdRemaining, format = true }: { invBomNM: number, currInv: number, sales: number, salesPlan: number, mdRemaining: number, format?: boolean }): number | string {
        const expectedSales = salesPlan - sales < 0 ? 0 : salesPlan - sales;
        const inflowUpdated = invBomNM - (currInv - (expectedSales + mdRemaining));
        return format ? ReportService.formatCurrency(inflowUpdated) : inflowUpdated;
    }

    // Calculate Inflow Trend
    static calculateInflowTrend({ invBomNM, currInv, sales, salesPlan, mdRemaining, trend, format = true }: { invBomNM: number, currInv: number, sales: number, salesPlan: number, mdRemaining: number, trend: number, format?: boolean }): number | string {
        if (trend === 0) {
            return ReportService.calculateInflowUpdated({ invBomNM, currInv, sales, salesPlan, mdRemaining, format });
        }
        const trendSales = sales / trend;
        const inflowTrend = invBomNM - currInv + trendSales - sales + mdRemaining;
        return format ? ReportService.formatCurrency(inflowTrend) : inflowTrend;
    }

    // Retail -> cost factor for trended inflow / trended OTB. OTB Recap, Inventory Action and POD
    // must all use this rule so their cost trends agree: cost complement MTD, or 1 (cost = retail)
    // when the class has no cost complement.
    static resolveTrendCostFactor(costComplementMtd: number | null | undefined): number {
        return costComplementMtd && costComplementMtd > 0 ? costComplementMtd : 1;
    }

    // Calculate Inflow Trend at cost. Apply per location + class, then sum; never multiply an
    // aggregated retail trend by a single row's cost complement.
    static calculateInflowTrendCost({ invBomNM, currInv, sales, salesPlan, mdRemaining, trend, costComplementMtd }: { invBomNM: number, currInv: number, sales: number, salesPlan: number, mdRemaining: number, trend: number, costComplementMtd: number | null | undefined }): number {
        const inflowTrend = ReportService.calculateInflowTrend({ invBomNM, currInv, sales, salesPlan, mdRemaining, trend, format: false }) as number;
        return inflowTrend * ReportService.resolveTrendCostFactor(costComplementMtd);
    }

    // Calculate trended OTB at cost: inflow trend at cost minus on order at cost.
    static calculateTrendedOtbCost({ invBomNM, currInv, sales, salesPlan, mdRemaining, onOrderCost, trend, costComplementMtd }: { invBomNM: number, currInv: number, sales: number, salesPlan: number, mdRemaining: number, onOrderCost: number, trend: number, costComplementMtd: number | null | undefined }): number {
        return ReportService.calculateInflowTrendCost({ invBomNM, currInv, sales, salesPlan, mdRemaining, trend, costComplementMtd }) - onOrderCost;
    }

    // Calculate Cash Margin
    static calculateCashMargin({ sales, inflowCost, format = true }: { sales: number, inflowCost: number, format?: boolean }): number | string {
        const cashMargin = sales - inflowCost;
        return format ? ReportService.formatCurrency(cashMargin) : cashMargin;
    }

    // Calculate Gross Margin
    static calculateGrossMargin({ sales, markdowns, costComplement, imu, format = true }: { sales: number, markdowns: number, costComplement: number, imu: number, format?: boolean }): number | string {
        if (sales === 0) return format ? '$0' : 0;
        const mdCost = (markdowns / sales) * costComplement;
        const grossMargin = sales * (imu - mdCost)
        return format ? ReportService.formatCurrency(grossMargin) : grossMargin;
    }

    // Calculate CMROI, formatted as %.1f
    static calculateCmroi({ sales, inflowCost, invAvgCost, format = true }: { sales: number, inflowCost: number, invAvgCost: number, format?: boolean }): number | string {
        if (invAvgCost === 0) return format ? '0.0' : 0;
        const cashMargin = ReportService.calculateCashMargin({ sales, inflowCost, format: false }) as number;
        const cmroi = cashMargin / invAvgCost;
        return format ? cmroi.toFixed(1) : cmroi;
    }

    // Calculate GMROI, formatted as %.1f
    static calculateGmroi({ sales, markdowns, costComplement, imu, invAvgCost, format = true }: { sales: number, markdowns: number, costComplement: number, imu: number, invAvgCost: number, format?: boolean }): number | string {
        if (invAvgCost === 0) return format ? '0.0' : 0;
        const grossMargin = ReportService.calculateGrossMargin({ sales, markdowns, costComplement, imu, format: false }) as number;
        const gmroi = grossMargin / invAvgCost;
        return format ? gmroi.toFixed(1) : gmroi;
    }

    // Calculate Turn
    static calculateTurn({ sales, invAvg, format = true }: { sales: number, invAvg: number, format?: boolean }): number | string {
        if (invAvg === 0) return format ? '0.0' : 0;
        const turn = (sales / invAvg);
        return format ? turn.toFixed(1) : turn;
    }

    // Calculate Efficiency
    static calculateEfficiency({ sales, inventory, inflowRetail, format = true }: { sales: number, inventory: number, inflowRetail: number, format?: boolean }): number | string {
        if (inventory + inflowRetail === 0) return format ? '0.0' : 0;
        const efficiency = (sales / (inventory + inflowRetail)) * 100;
        return format ? efficiency.toFixed(1) + '%' : efficiency;
    }

    // Calculate Freshness
    static calculateFreshness({ inflowRetail, inventory, format = true }: { inflowRetail: number, inventory: number, format?: boolean }): number | string {
        if (inventory === 0) return format ? '0.0' : 0;
        const freshness = (inflowRetail / inventory) * 100;
        return format ? freshness.toFixed(1) + '%' : freshness;
    }
}

export default ReportService;
